/**
 * MAHI — her illustrated section marks: the wardrobe's dress on a hanger, the
 * Almost Yours tote, the chat-history bubbles, the Style DNA helix, the
 * sliders for What you see.
 *
 * One mark per section, used everywhere that section is named. Discover shows
 * the wardrobe twice on one screen (the save button in the action row, the
 * counter top-right) and the profile panel names all three; those used to be
 * lucide glyphs, several of them different from each other for the same
 * destination, so the button you pressed and the place it took you never quite
 * looked like the same thing. These are that one thing.
 *
 * Each mark is one small WebP in the product-image bucket — the same S3 storage
 * the wardrobe's own item photos come from — served through CloudFront where
 * the CDN is configured and straight from S3 where it is not. They render in
 * 36-44px circles, so 256px is already generous at 3x and there is no larger
 * variant to choose between: one file, about 8KB, per mark.
 *
 * Keys are versioned (`-v1-`, `-v2-`) and uploaded `immutable`, so a redrawn
 * mark ships under the next number rather than waiting out an edge cache.
 *
 * If an image cannot load — offline, blocked, a bucket misconfigured after a
 * deploy — the mark falls back to the glyph it replaced rather than leaving a
 * hole where a navigation affordance should be.
 */
import React, { useState } from 'react';
import { Heart, ListFilter, MessageCircle, Radar, ShoppingBag, X } from 'lucide-react';

// Empty in local dev (see .env.example), which is why the fallback is the
// direct public S3 origin rather than a relative path.
const CDN_BASE = (process.env.REACT_APP_IMAGE_CDN_BASE || '').replace(/\/$/, '');
const ORIGIN = CDN_BASE || 'https://nanokart-ai-product-img-store.s3.ap-south-1.amazonaws.com';

/**
 * `className` sizes it. The artwork is a full-bleed circle, so it is meant to
 * fill a round slot edge to edge rather than sit inside one as a small glyph.
 */
function BrandMark({ file, fallback, className = 'w-full h-full' }) {
  const [failed, setFailed] = useState(false);
  if (failed) return React.cloneElement(fallback, { className });
  return (
    <img
      src={`${ORIGIN}/mahi/brand/${file}`}
      alt=""
      aria-hidden="true"
      draggable={false}
      decoding="async"
      onError={() => setFailed(true)}
      className={`${className} object-cover rounded-full select-none pointer-events-none`}
    />
  );
}

// The hanger the wardrobe mark replaced, kept as its failure fallback only.
const Hanger = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d="M12 7.5a2.25 2.25 0 1 1 2.25-2.25c0 .9-.6 1.4-1.3 1.8-.55.32-.95.6-.95 1.2v.9"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M12 9.4 4.6 14.7c-.9.64-.45 2.05.65 2.05h13.5c1.1 0 1.55-1.41.65-2.05L12 9.4Z"
      stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

export const WardrobeMark = ({ className }) => (
  <BrandMark file="wardrobe-icon-v2-256.webp" fallback={<Hanger />} className={className} />
);

// The right-swipe/save button's own mark — deliberately distinct from
// WardrobeMark now, so "like" reads as its own verb rather than a duplicate
// of the wardrobe destination it happens to save into.
export const LikeMark = ({ className }) => (
  <BrandMark
    file="like-icon-v1-256.webp"
    fallback={<Heart strokeWidth={1.75} />}
    className={className}
  />
);

// The left-swipe/pass button's own mark, paired with LikeMark.
export const DislikeMark = ({ className }) => (
  <BrandMark
    file="dislike-icon-v1-256.webp"
    fallback={<X strokeWidth={1.75} />}
    className={className}
  />
);

export const AlmostYoursMark = ({ className }) => (
  <BrandMark
    file="almost-yours-icon-v1-256.webp"
    fallback={<ShoppingBag strokeWidth={1.75} />}
    className={className}
  />
);

export const ChatHistoryMark = ({ className }) => (
  <BrandMark
    file="chat-history-icon-v1-256.webp"
    fallback={<MessageCircle strokeWidth={1.75} />}
    className={className}
  />
);

export const StyleDnaMark = ({ className }) => (
  <BrandMark
    file="style-dna-icon-v1-256.webp"
    fallback={<Radar strokeWidth={1.75} />}
    className={className}
  />
);

export const WhatYouSeeMark = ({ className }) => (
  <BrandMark
    file="what-you-see-icon-v1-256.webp"
    fallback={<ListFilter strokeWidth={1.75} />}
    className={className}
  />
);
